import { ClassItem } from "@/data/classes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Users, Bell, GraduationCap, ListPlus, ArrowRight, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNextRegistrationPeriodLabel } from "@/hooks/useNextRegistrationPeriod";
import { semesterTotalForSession } from "@/lib/semesterSessionPrice";
import { formatMoney } from "@/lib/classPricing";
import { publicCtaFor } from "@/lib/publicVisibility";
import { formatPublicSchedule, publicPeriodSuffix } from "@/lib/publicScheduleDisplay";


interface ClassCardProps {
  classItem: ClassItem;
  onClick: (classItem: ClassItem) => void;
  additionalOptionsCount?: number;
  /** Live-derived "New & Enrolling" highlight. Never stored on the class. */
  highlightNewAndEnrolling?: boolean;
}

// One badge system — identical size, padding, typography, placement everywhere.
const BADGE_BY_STATUS: Record<string, { label: string; className: string }> = {
  open: {
    label: "Open",
    className: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  ended: {
    label: "Ended",
    className: "bg-neutral-100 text-neutral-700 border-neutral-200",
  },
  "coming-soon": {
    label: "Coming Soon",
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
  "instructor-wanted": {
    label: "Instructor Wanted",
    className: "bg-yellow-50 text-yellow-800 border-yellow-200",
  },
  waitlist: {
    label: "Waitlist",
    className: "bg-purple-50 text-purple-800 border-purple-200",
  },
};

const CTA_BY_STATUS: Record<string, { label: string; icon: typeof ArrowRight }> = {
  open: { label: "Register Now", icon: ArrowRight },
  "coming-soon": { label: "Notify Me", icon: Bell },
  waitlist: { label: "Join Waitlist", icon: ListPlus },
  // Instructor recruitment never replaces the parent pathway — the primary
  // action stays parent-facing and "Teach This Class" sits alongside it.
  "instructor-wanted": { label: "I'm Interested", icon: Bell },
  ended: { label: "View Details", icon: ArrowRight },
};


// Schedule text and CTA period labels come from the shared public display
// rules — no inferred semesters, "TBD" when nothing is finalized.

/** Understated, branded category illustrations used when a real photo is missing. */
function categoryPlaceholder(category: string | undefined): string {
  const c = (category ?? "").toLowerCase();
  let motif = `<path d='M0 40 C 0 0, -30 -20, -55 -20 C -50 10, -25 40, 0 40 Z' fill='#8fa878' fill-opacity='0.35'/>
    <path d='M0 40 C 0 0, 30 -20, 55 -20 C 50 10, 25 40, 0 40 Z' fill='#8fa878' fill-opacity='0.35'/>
    <path d='M0 40 V -30'/>`;
  if (/(nature|science|farm|animal|outdoor)/.test(c)) {
    motif = `<path d='M-70 30 L -30 -20 L 0 15 L 30 -30 L 70 30 Z' fill='#8fa878' fill-opacity='0.35'/>
      <circle cx='40' cy='-35' r='10' fill='#e6c98a' fill-opacity='0.7'/>`;
  } else if (/(art|creative|craft|music|dance)/.test(c)) {
    motif = `<circle cx='0' cy='0' r='38' fill='#e6c98a' fill-opacity='0.35'/>
      <circle cx='-16' cy='-8' r='5' fill='#5f7a4b'/>
      <circle cx='14' cy='-6' r='5' fill='#a06a4a'/>
      <circle cx='-6' cy='16' r='5' fill='#8fa878'/>
      <path d='M40 30 L 70 60' stroke='#5f7a4b' stroke-width='6' stroke-linecap='round'/>`;
  } else if (/(strategy|logic|chess|game)/.test(c)) {
    motif = `<rect x='-45' y='20' width='90' height='10' fill='#5f7a4b' fill-opacity='0.5'/>
      <path d='M-10 20 L -10 -10 Q 0 -30 10 -10 L 10 20 Z' fill='#8fa878' fill-opacity='0.5'/>
      <circle cx='0' cy='-20' r='10' fill='#8fa878' fill-opacity='0.7'/>`;
  } else if (/(stem|robot|tech|engineer|mech|auto)/.test(c)) {
    motif = `<circle cx='0' cy='0' r='28' fill='none' stroke='#5f7a4b' stroke-width='6'/>
      <circle cx='0' cy='0' r='10' fill='#5f7a4b' fill-opacity='0.6'/>
      <g stroke='#5f7a4b' stroke-width='6' stroke-linecap='round'>
        <path d='M0 -40 V -28'/><path d='M0 40 V 28'/><path d='M-40 0 H -28'/><path d='M40 0 H 28'/>
      </g>`;
  } else if (/(physical|sport|fitness|pe|basketball|volleyball|dance)/.test(c)) {
    motif = `<circle cx='0' cy='0' r='32' fill='none' stroke='#5f7a4b' stroke-width='6'/>
      <path d='M-32 0 H 32 M0 -32 V 32 M-22 -22 Q 0 0 22 22 M22 -22 Q 0 0 -22 22' stroke='#5f7a4b' stroke-width='4' fill='none'/>`;
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'>
    <defs>
      <linearGradient id='bg' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='#f5f0e6'/>
        <stop offset='100%' stop-color='#dbe4d2'/>
      </linearGradient>
    </defs>
    <rect width='400' height='300' fill='url(#bg)'/>
    <g transform='translate(200 140)' fill='none' stroke='#5f7a4b' stroke-width='5' stroke-linecap='round' stroke-linejoin='round'>${motif}</g>
    <text x='50%' y='250' text-anchor='middle' font-family='Georgia, serif' font-size='16' fill='#5f7a4b' font-style='italic'>Silver Sage</text>
  </svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}

const ClassCard = ({ classItem, onClick, additionalOptionsCount = 0, highlightNewAndEnrolling = false }: ClassCardProps) => {
  const navigate = useNavigate();
  const isExternal = classItem.managementType === "external_provider";
  const isComingSoon = classItem.status === "coming-soon" && !isExternal;
  const isInstructorWanted = classItem.status === "instructor-wanted" && !isExternal;
  const isWaitlist = classItem.status === "waitlist";
  const isOpen = classItem.status === "open" || isExternal;
  const ctaRules = publicCtaFor(classItem.status, isExternal);
  const nextPeriodLabel = useNextRegistrationPeriodLabel();


  const openDetail = () => onClick(classItem);
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(); }
  };
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const badge = isExternal
    ? { label: "Privately Managed", className: "bg-sky-50 text-sky-800 border-sky-200" }
    : (BADGE_BY_STATUS[classItem.status] ?? BADGE_BY_STATUS.open);
  const cta = isExternal
    ? { label: "Register with Provider", icon: ExternalLink }
    : (CTA_BY_STATUS[classItem.status] ?? CTA_BY_STATUS.open);
  const CtaIcon = cta.icon;
  const scheduleText = formatPublicSchedule(classItem);
  const periodSuffix = publicPeriodSuffix(classItem, nextPeriodLabel);
  // Semester sessions show their canonical total ("Fall Semester · $120") using
  // the shared pricing resolver — never a hardcoded amount.
  const semesterLines = (classItem.sessions ?? [])
    .map((s) => {
      const total = semesterTotalForSession(classItem, s);
      return total == null ? null : `${s.label} · ${formatMoney(total)}`;
    })
    .filter((l): l is string => !!l);
  const placeholder = categoryPlaceholder(classItem.category);
  const isGenericPlaceholder =
    typeof classItem.image === "string" &&
    classItem.image.startsWith("data:image/svg+xml");
  const imageSrc = isGenericPlaceholder ? placeholder : (classItem.image || placeholder);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={handleKey}
      className="group h-full text-left bg-card rounded-2xl overflow-hidden border border-border/70 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ease-out cursor-pointer w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary flex flex-col"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        <img
          src={imageSrc}
          onError={(e) => {
            const el = e.currentTarget;
            if (el.src !== placeholder) el.src = placeholder;
          }}
          alt={classItem.title}
          className="w-full h-full object-cover transition-all duration-300 ease-out group-hover:scale-[1.03] group-hover:brightness-105"
          loading="lazy"
        />
        <Badge
          className={`absolute top-3 right-3 border ${badge.className} font-body font-medium text-[11px] tracking-wide px-2.5 py-0.5 rounded-full shadow-sm`}
        >
          {badge.label}
        </Badge>
        {highlightNewAndEnrolling && (
          <Badge
            className="absolute top-3 left-3 border bg-background/95 text-primary border-primary/30 font-body font-medium text-[11px] tracking-wide px-2.5 py-0.5 rounded-full shadow-sm"
          >
            New &amp; Enrolling
          </Badge>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1 gap-3">
        <div className="space-y-1.5">
          <p className="text-[10.5px] font-body font-semibold uppercase tracking-[0.16em] text-primary/70">
            {classItem.category}
          </p>
          <h3 className="font-heading text-lg sm:text-xl font-bold text-foreground leading-snug line-clamp-2 min-h-[3rem]">
            {classItem.title}
          </h3>
        </div>

        {(isExternal
          ? (classItem.providerPublicContactText ??
             "Registration and instruction are managed directly by the class provider.")
          : classItem.shortDescription) && (
          <p className="text-sm text-muted-foreground/90 line-clamp-2 min-h-[2.5rem]">
            {isExternal
              ? (classItem.providerPublicContactText ??
                 "Registration and instruction are managed directly by the class provider.")
              : classItem.shortDescription}
          </p>
        )}

        <div className="pt-3 mt-auto border-t border-border/50 space-y-1.5 text-[13px] text-muted-foreground">
          <div className="flex items-center gap-2 truncate">
            <Users size={13} className="shrink-0 opacity-60" />
            <span className="truncate">Ages {classItem.ages}</span>
          </div>
          <div className="flex items-center gap-2 truncate">
            <Clock size={13} className="shrink-0 opacity-60" />
            <span className="truncate">{scheduleText}</span>
          </div>
          <div className="flex items-center gap-2 truncate">
            <Calendar size={13} className="shrink-0 opacity-60" />
            <span className="truncate">
              {classItem.duration}
              {classItem.price ? <span className="opacity-50"> · </span> : null}
              {classItem.price && <span>{classItem.price}</span>}
            </span>
          </div>
          {semesterLines.map((line) => (
            <div key={line} className="flex items-center gap-2 truncate">
              <Calendar size={13} className="shrink-0 opacity-60" />
              <span className="truncate">{line}</span>
            </div>
          ))}
        </div>

        {additionalOptionsCount > 0 && (
          <div className="rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2 text-[12px] text-sky-900">
            <span className="font-semibold">+{additionalOptionsCount} potential future {additionalOptionsCount === 1 ? "session" : "sessions"}</span>
            <span className="opacity-80"> · tap to view</span>
          </div>
        )}

        <Button
          type="button"
          variant={isOpen ? "default" : "outline"}
          className="w-full mt-1 font-body font-semibold rounded-full"
          onClick={(e) => {
            stop(e);
            if (isExternal && classItem.externalSignupUrl) {
              window.open(classItem.externalSignupUrl, "_blank", "noopener,noreferrer");
              return;
            }
            openDetail();
          }}
        >
          <CtaIcon size={16} className="mr-2" />
          {isComingSoon && periodSuffix ? `${cta.label} · ${periodSuffix}` : cta.label}
        </Button>

        {ctaRules.teach && (
          <Button
            type="button"
            variant="ghost"
            className="w-full font-body font-semibold rounded-full text-primary"
            onClick={(e) => {
              stop(e);
              navigate(`/want-to-teach?class=${encodeURIComponent(classItem.title)}`);
            }}
          >
            <GraduationCap size={16} className="mr-2" />
            Teach This Class
          </Button>
        )}

      </div>
    </div>
  );
};

export default ClassCard;
